import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Camera, ExternalLink, Sparkles, X } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { api, type StampCollectedSpot } from '../services/api'
import { gotoSummon } from '../lib/arLaunch'
import { arWebArUrl } from '../lib/features'
import CharacterPreview from './CharacterPreview'

/**
 * 集めたキャラの一覧。
 *
 * **スタンプの数だけでは何を見つけたのか分からない。** 2D / 3D / 動画のキャラを
 * 用意しても、その場で消えてしまい、見返す理由も人に見せる理由も生まれない
 * (スクショして SNS に上げてもらう導線としても数字だけでは弱い)。
 *
 * AR から戻った直後と、特典チケットの画面の**両方**で使う。達成した瞬間が
 * 一番スクショされるので、そこに何を集めたかが写るようにする。
 */
export default function CollectedCharacters({ eventId }: { eventId: string }) {
  const { t } = useI18n()
  const [spots, setSpots] = useState<StampCollectedSpot[]>([])
  /** 開いているキャラ。**小さなカードでは 3D も動画も分からない** —
   *  押したら大きく見られるようにする (集めた実感と、スクショの見栄え)。 */
  const [opened, setOpened] = useState<StampCollectedSpot | null>(null)

  useEffect(() => {
    let cancelled = false
    // **取れなくても本題は成立する**ので、失敗しても黙って諦める。ここで
    // エラーを出すと、スタンプが増えたこと自体が見えなくなる。
    api
      .getCollectedSpots(eventId)
      .then(({ spots }) => { if (!cancelled) setSpots(spots) })
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [eventId])

  if (spots.length === 0) return null

  return (
    <div className="w-full animate-fade-in">
      <p className="mb-2 font-body text-xs text-text-tertiary">
        {t('stampRally.arCollectedTitle')}
      </p>
      <div className="space-y-2">
        {spots.map((spot) => (
          <button
            key={spot.id}
            onClick={() => setOpened(spot)}
            className="flex w-full items-center gap-3 rounded-xl bg-white p-3 text-left shadow-card tap-effect"
          >
            {spot.characterUrl ? (
              <CharacterPreview url={spot.characterUrl} size="h-16 w-16 shrink-0" />
            ) : (
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-gray-50">
                <Sparkles size={20} className="text-text-tertiary" />
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate font-body font-medium text-text-primary">{spot.name}</p>
              {spot.creatorName && (
                <p className="mt-0.5 truncate font-body text-[11px] text-text-tertiary">
                  {t('stampRally.arCreationBy', { name: spot.creatorName })}
                </p>
              )}
              {spot.sponsorName && (
                <p className="mt-0.5 truncate font-body text-[11px] text-text-tertiary">
                  {t('stampRally.sponsoredBy', { name: spot.sponsorName })}
                </p>
              )}
              {spot.description && (
                <p className="mt-0.5 line-clamp-2 font-body text-xs text-text-secondary">
                  {spot.description}
                </p>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* **body 直下に出す。** 親に transform/opacity があると重なり順がそこに
          閉じ込められ、後ろのスタンプがモーダルの上に載ってしまう (実際になった)。 */}
      {opened && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-6"
          onClick={() => setOpened(null)}
        >
          <div
            className="w-full max-w-[360px] rounded-2xl bg-white p-5 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end">
              <button onClick={() => setOpened(null)} className="tap-effect rounded-lg p-1 text-text-tertiary" aria-label={t('common.close')}>
                <X size={18} />
              </button>
            </div>
            {opened.characterUrl && (
              <div className="mx-auto w-fit">
                {/* 大きく出す。3D は回り、動画は再生される (小さいと区別が付かない) */}
                <CharacterPreview url={opened.characterUrl} size="h-56 w-56" detail />
              </div>
            )}
            <p className="mt-3 font-display text-lg text-navy">{opened.name}</p>
            {opened.creatorName && (
              <p className="mt-0.5 font-body text-xs text-text-tertiary">
                {t('stampRally.arCreationBy', { name: opened.creatorName })}
              </p>
            )}
            {opened.description && (
              <p className="mt-1 font-body text-sm text-text-secondary">{opened.description}</p>
            )}
            {opened.prLinkUrl && (
              <a
                href={opened.prLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 font-body text-sm text-navy tap-effect"
              >
                {t('stampRally.arPrMore')}
                <ExternalLink size={14} />
              </a>
            )}
            {/* 召喚: あつめたキャラを、カメラの前に呼び出す。**捕まえた後の遊び** —
                家でも、イベントが終わった後でも呼び出せる (位置情報もトークンも要らない)。
                AR を配信していない環境 (VITE_AR_WEBAR_URL 未設定) では出さない */}
            {opened.characterUrl && arWebArUrl() && (
              <div className="mt-4">
                <button
                  onClick={() => gotoSummon(opened.id)}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-yokoso-red py-3 font-body text-sm font-medium text-white tap-effect"
                >
                  <Camera size={16} />
                  {t('stampRally.arSummon')}
                </button>
                <p className="mt-1.5 font-body text-[11px] text-text-tertiary">
                  {t('stampRally.arSummonHint')}
                </p>
              </div>
            )}
            {opened.sponsorName && (
              <div className="mt-4 border-t border-gray-100 pt-3">
                <p className="font-body text-[11px] text-text-tertiary">
                  {t('stampRally.sponsoredBy', { name: opened.sponsorName })}
                </p>
                {opened.sponsorLinkUrl && (
                  <a
                    href={opened.sponsorLinkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 font-body text-sm text-navy tap-effect"
                  >
                    {t('stampRally.sponsorLink')}
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
